import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import {applicationApi} from '../api';
import {AppealMaterialModel, ApplicationDetailModel, ApplicationListItemModel, ApplicationMediaModel, MediaKind, PagedView} from '../model';
import {useNotifications} from '../../../shared';

export const useMyApplications = (): UseInfiniteQueryResult<InfiniteData<PagedView<ApplicationListItemModel>>, Error> => {
  return useInfiniteQuery<
    PagedView<ApplicationListItemModel>,
    Error,
    InfiniteData<PagedView<ApplicationListItemModel>>,
    ['my-applications'],
    string | undefined
  >({
    queryKey: ['my-applications'],
    queryFn: ({pageParam}) => applicationApi.getMyApplications({after: pageParam}),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage ? (lastPage.pageInfo.endCursor ?? undefined) : undefined;
    },
  });
};

export const useMyApplication = (appealId: string): UseQueryResult<ApplicationDetailModel, Error> => {
  return useQuery({
    queryKey: ['my-application', appealId],
    queryFn: () => applicationApi.getMyApplication(appealId),
    enabled: !!appealId,
  });
};

export const useApplicationMedia = (appealId: string): UseInfiniteQueryResult<InfiniteData<PagedView<ApplicationMediaModel>>, Error> => {
  return useInfiniteQuery<
    PagedView<ApplicationMediaModel>,
    Error,
    InfiniteData<PagedView<ApplicationMediaModel>>,
    ['application-media', string],
    string | undefined
  >({
    queryKey: ['application-media', appealId],
    queryFn: ({pageParam}) => applicationApi.getApplicationMedia(appealId, {after: pageParam}),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage ? (lastPage.pageInfo.endCursor ?? undefined) : undefined;
    },
    enabled: !!appealId,
  });
};

export const useAppealMaterials = (appealId: string): UseInfiniteQueryResult<InfiniteData<PagedView<AppealMaterialModel>>, Error> => {
  return useInfiniteQuery<
    PagedView<AppealMaterialModel>,
    Error,
    InfiniteData<PagedView<AppealMaterialModel>>,
    ['appeal-materials', string],
    string | undefined
  >({
    queryKey: ['appeal-materials', appealId],
    queryFn: ({pageParam}) => applicationApi.getAppealMaterials(appealId, {after: pageParam}),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage ? (lastPage.pageInfo.endCursor ?? undefined) : undefined;
    },
    enabled: !!appealId,
  });
};

export const useAddMedia = (appealId: string): UseMutationResult<ApplicationMediaModel, Error, {uploadId: string; kind: MediaKind; caption?: string}> => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  return useMutation({
    mutationFn: ({uploadId, kind, caption}) => applicationApi.addMedia(appealId, uploadId, kind, caption),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['application-media', appealId]});
      queryClient.invalidateQueries({queryKey: ['my-applications']});
      createNotification({title: 'Медиа успешно добавлено', name: 'addMedia', theme: 'success'});
    },
  });
};

export const useDeleteMedia = (appealId: string): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  return useMutation({
    mutationFn: (mediaId: string) => applicationApi.deleteMedia(appealId, mediaId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['application-media', appealId]});
      queryClient.invalidateQueries({queryKey: ['my-applications']});
      createNotification({title: 'Медиа успешно удалено', name: 'deleteMedia', theme: 'success'});
    },
    onError: () => {
      createNotification({title: 'Ошибка при удалении медиа', name: 'deleteMediaError', theme: 'danger'});
    },
  });
};

export const useUpdateCaption = (appealId: string): UseMutationResult<void, Error, {mediaId: string; caption: string}> => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  return useMutation({
    mutationFn: ({mediaId, caption}) => applicationApi.updateCaption(appealId, mediaId, caption),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['application-media', appealId]});
      createNotification({title: 'Подпись успешно обновлена', name: 'updateCaption', theme: 'success'});
    },
    onError: () => {
      createNotification({title: 'Ошибка при обновлении подписи', name: 'updateCaptionError', theme: 'danger'});
    },
  });
};

export const useAddAppealMaterial = (
  appealId: string,
): UseMutationResult<AppealMaterialModel, Error, {uploadId: string; kind: MediaKind; caption?: string}> => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  return useMutation({
    mutationFn: ({uploadId, kind, caption}) => applicationApi.addAppealMaterial(appealId, uploadId, kind, caption),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['appeal-materials', appealId]});
      createNotification({title: 'Материал успешно добавлен', name: 'addAppealMaterial', theme: 'success'});
    },
    onError: () => {
      createNotification({title: 'Ошибка при добавлении материала', name: 'addAppealMaterialError', theme: 'danger'});
    },
  });
};

export const useDeleteAppealMaterial = (appealId: string): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  return useMutation({
    mutationFn: (materialId: string) => applicationApi.deleteAppealMaterial(appealId, materialId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['appeal-materials', appealId]});
      createNotification({title: 'Материал успешно удалён', name: 'deleteAppealMaterial', theme: 'success'});
    },
    onError: () => {
      createNotification({title: 'Ошибка при удалении материала', name: 'deleteAppealMaterialError', theme: 'danger'});
    },
  });
};

export const useUpdateAppealMaterialCaption = (
  appealId: string,
): UseMutationResult<void, Error, {materialId: string; caption: string}> => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  return useMutation({
    mutationFn: ({materialId, caption}) => applicationApi.updateAppealMaterialCaption(appealId, materialId, caption),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['appeal-materials', appealId]});
      createNotification({title: 'Подпись успешно обновлена', name: 'updateAppealMaterialCaption', theme: 'success'});
    },
    onError: () => {
      createNotification({title: 'Ошибка при обновлении подписи', name: 'updateAppealMaterialCaptionError', theme: 'danger'});
    },
  });
};
